﻿import type { Nullable } from '@src/shared/types/common';

import type { IToastrOptions } from '../types/modules/toastr';

function installToastr() {
    window.toastr = {
        clear: (div: JQuery) => {
            removeToastrInDom(div);
        },
        warning: (content: string, options: IToastrOptions = {}) => {
            buildToastr('warning', content, options);
        },
        error: (content: string, options: IToastrOptions = {}) => {
            buildToastr('error', content, options);
        },
        info: (content: string, options: IToastrOptions = {}) => {
            buildToastr('info', content, options);
        },
        success: (content: string, options: IToastrOptions = {}) => {
            buildToastr('success', content, options);
        }
    };
}

function buildToastr(type: string, content: string, options: IToastrOptions = {}) {
    const defaultOptions: Required<Omit<IToastrOptions, 'onclick'>> = {
        imgVisible: true,
        classList: '',
        delay: 5000,
        delayMouseout: 1000,
        preventDuplicates: false,
        ...options
    };

    const newToastr = createToastr(type, content, defaultOptions);
    if (newToastr === null) {
        return;
    }

    showToastr(newToastr, defaultOptions.delay);
    addEventForToastr(newToastr, defaultOptions);
}

function createToastr(type: string, content: string, options: IToastrOptions = {}) {
    const classList = [`toast__${type}`];

    if (!options.imgVisible) {
        classList.push('toast__img-disabled');
    }

    const div = $(`
        <div class="toast ${classList.join(' ')} ${options.classList} position-relative" data-bs-autohide="false">
            <div class="toast_container">${content}</div>
        </div>
    `);
    if (options.preventDuplicates && getToastrDuplicates($(content).html()).length > 0) {
        return null;
    }

    let container = $('.toast-container');
    if (container.length === 0) {
        container = $(`<div class="toast-container position-fixed toast__top-right">`);
        $('body').append(container);
    }

    container.append(div);
    return div;
}

function getToastrDuplicates(content: string): JQuery {
    return $('.toast > .toast_container').filter(function () {
        return $(this).html().indexOf(content) !== -1;
    });
}

function removeToastrInDom(div: JQuery) {
    div.off('hidden.bs.toast click mouseout');
    div.fadeOut('slow', () => {
        div.remove();
        const container = $('.toast-container');
        if (container.height() === 0) {
            container.remove();
        }
    });
}

function showToastr(div: JQuery, delay: number) {
    div.fadeIn('slow');
    setTimeoutRemoveToastr(div, delay);
}

function setTimeoutRemoveToastr(div: JQuery, delay: number) {
    if (delay === 0) {
        return;
    }
    const idSetTimeout = setTimeout(() => {
        removeToastrInDom(div);
    }, delay);
    div.attr('data-timeout', String(idSetTimeout));
}

function addEventForToastr(div: JQuery, options: IToastrOptions = {}) {
    div
        .on('click', () => {
            options.onclick?.();
            removeToastrInDom(div);
        })
        .on('mouseover', () => {
            const idTimeout = Number(div.attr('data-timeout'));

            if (idTimeout && !isNaN(idTimeout)) {
                div.removeAttr('data-timeout');
                clearTimeout(idTimeout);
            }
        })
        .on('mouseout', (event) => {
            const relatedTarget = event.relatedTarget as Nullable<HTMLElement | undefined>;

            if (!relatedTarget?.closest('.toast')) {
                setTimeoutRemoveToastr(div, options.delayMouseout ?? 200);
            }
        });
}

window.buildToastr = buildToastr;
window.installToastr = installToastr;
window.addEventForToastr = addEventForToastr;
window.getToastrDuplicates = getToastrDuplicates;
window.createToastr = createToastr;
window.showToastr = showToastr;
window.setTimeoutRemoveToastr = setTimeoutRemoveToastr;
window.removeToastrInDom = removeToastrInDom;
